/* eslint-disable max-lines */
import React from "react";
import { graphql } from "gatsby";
import { HeaderNavProvider, StyledFooterProvider } from "tr-web";
import {
  TaskTemplateServiceTemplateQuery,
  SitePageContext,
} from "../../graphql-types";
import { useHowItWorksProps } from "./SharedServiceTemplate/HowItWorks/use-how-it-works-query";
import { PageBase } from "../shared/components/PageBase";
import { useSMSDownloadLinkModuleProps } from "../tr-web/SMSDownloadLinkModule/use-sms-download-link-module-props";
import { selectHeaderNavProps } from "../tr-web/HeaderNav/task-template-select-header-nav-props";
import { TaskTemplateService } from "./TaskTemplateServiceTemplate/TaskTemplateService";
import { selectHeroProps } from "./SharedServiceTemplate/task-template-select-hero-props";
import { selectPageBaseProps } from "./TaskTemplateServiceTemplate/select-page-base-props";
import { selectTaskTemplateServiceBreadcrumbsProps } from "./TaskTemplateServiceTemplate/select-task-template-service-breadcrumbs-props";
import { selectTaskTemplateServiceTextModuleProps } from "./TaskTemplateServiceTemplate/select-task-template-service-text-module-props";
import { useReadyToHireProps } from "./TaskTemplateServiceTemplate/use-ready-to-hire-props";

import { Metrics } from "./TaskTemplateServiceTemplate/Metrics";

export const pageQuery = graphql`
  query TaskTemplateServiceTemplate($id: String!) {
    taskTemplateServicePage(id: { eq: $id }) {
      callToActionUrl
      canonicalUrl
      displayCta
      faqs {
        question
        answer
      }
      heroButtonPath
      heroSubtitle
      heroTitle
      hreflangData {
        locale
        url
      }
      id
      locale
      marketingGroupIndexPath
      marketingGroupTitle
      noIndexMeta
      pageDescription
      pageTitle
      path
      serviceIndexPath
      serviceLinks {
        path
        title
      }
      templateName
      textModuleDescription
      textModuleImage {
        fluid {
          ...CloudinaryAssetFluid_tracedSVG
        }
        fixed(width: 9999) {
          ...CloudinaryAssetFixed_tracedSVG
        }
      }
      textModuleSectionTitle
      widePhoto {
        fluid {
          ...CloudinaryAssetFluid_tracedSVG
        }
        fixed(width: 9999) {
          ...CloudinaryAssetFixed_tracedSVG
        }
      }
    }
  }
`;

interface TaskTemplateServiceTemplateArgs {
  data: TaskTemplateServiceTemplateQuery;
  pageContext: SitePageContext;
}

function TaskTemplateServiceTemplate({
  data,
  pageContext,
}: TaskTemplateServiceTemplateArgs) {
  const howItWorksProps = useHowItWorksProps();
  const smsDownloadLinkModuleProps = useSMSDownloadLinkModuleProps();
  const headerNavProps = selectHeaderNavProps(data);
  const heroProps = selectHeroProps(data);
  const pageBaseProps = selectPageBaseProps(data);
  const taskTemplateServiceBreadcrumbsProps =
    selectTaskTemplateServiceBreadcrumbsProps({ data, pageContext });
  const taskTemplateServiceTextModuleProps =
    selectTaskTemplateServiceTextModuleProps(data);
  const faqsProps = data.taskTemplateServicePage?.faqs || [];
  const readyToHireProps = useReadyToHireProps(data);
  const serviceType = data?.taskTemplateServicePage?.templateName || "";

  return (
    <>
      <Metrics />
      <PageBase
        canonicalUrl={pageBaseProps.canonicalUrl}
        hreflangData={pageBaseProps.hreflangData}
        locale={pageBaseProps.locale}
        noIndexMeta={pageBaseProps.noIndexMeta}
        pageDescription={pageBaseProps.pageDescription}
        pageImage={pageBaseProps.pageImage}
        pageName="Task Template"
        pageTitle={pageBaseProps.pageTitle}
        serviceType={serviceType}
      >
        <HeaderNavProvider>
          <StyledFooterProvider>
            <TaskTemplateService
              faqsProps={faqsProps}
              headerNavProps={headerNavProps}
              heroProps={heroProps}
              howItWorksProps={howItWorksProps}
              readyToHireProps={readyToHireProps}
              smsDownloadLinkModuleProps={smsDownloadLinkModuleProps}
              taskTemplateServiceBreadcrumbsProps={
                taskTemplateServiceBreadcrumbsProps
              }
              taskTemplateServiceTextModuleProps={
                taskTemplateServiceTextModuleProps
              }
            />
          </StyledFooterProvider>
        </HeaderNavProvider>
      </PageBase>
    </>
  );
}

export default TaskTemplateServiceTemplate;
